import { sample } from 'effector';
import { isEqual } from 'lodash';
import { $token, signout } from '@features/common';
import { fxGetTicketList, pageMounted, pageUnmounted } from '../page/page.model';
import {
  $filters,
  defaultFilters,
  fxSetLocaleStorage,
  setFilters,
  $setFiltersCounter,
} from './filters.model';

const name = 'savedFiltersTicket';

const maxDayInactivity = 7;
const hoursInDay = 24;
const minutesInHours = 60;
const secondsInMinutes = 60;
const millisecondsInSeconds = 1000;

const timeOfInactivity =
  maxDayInactivity *
  hoursInDay *
  minutesInHours *
  secondsInMinutes *
  millisecondsInSeconds;

/** Сохранение фильтров */
sample({
  clock: fxGetTicketList,
  source: { filters: $filters, token: $token },
  filter: ({ token, filters }) => Boolean(token) && !isEqual(filters, defaultFilters),
  fn: ({ filters, token }) => {
    const values = localStorage.getItem(name) as string;
    const timestamp = new Date().getTime();

    return {
      key: name,
      data: JSON.stringify({
        ...JSON.parse(values),
        ...{ [token as string]: { filters, timestamp } },
      }),
    };
  },
  target: fxSetLocaleStorage,
});

const excludeFilters = (token: string) =>
  Object.entries(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    JSON.parse(localStorage.getItem(name) as string)
  ).reduce(
    (acc, [currentToken, data]) =>
      currentToken === token ? acc : { ...acc, ...{ [currentToken]: data } },

    {}
  );

/** Подстановка фильтров из сохраненных */
sample({
  clock: pageMounted,
  source: { filters: $filters, token: $token },
  filter: ({ token }) => {
    const currentTimestamp = new Date().getTime();

    const { timestamp } = JSON.parse(localStorage.getItem(name) as string)[
      token as string
    ];

    return Boolean(token) && currentTimestamp - timestamp <= timeOfInactivity;
  },
  fn: ({ filters, token }) => {
    const savedFilters = JSON.parse(localStorage.getItem(name) as string)[token as string]
      ?.filters;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return Object.entries(filters).reduce(
      (acc, [key, value]) =>
        key in savedFilters
          ? { ...acc, ...{ [key]: savedFilters[key] } }
          : { ...acc, ...{ [key]: value } },
      {}
    );
  },
  target: $filters,
});

/** Если фильтры сброшены, то очищаются из списка сохраненных */
sample({
  clock: fxGetTicketList,
  source: { filters: $filters, token: $token },
  filter: ({ token, filters }) => Boolean(token) && isEqual(filters, defaultFilters),
  fn: ({ token }) => ({
    key: name,
    data: JSON.stringify(excludeFilters(token as string)),
  }),
  target: fxSetLocaleStorage,
});

$setFiltersCounter
  .on(setFilters, (state) => state + 1)
  .reset()
  .reset([pageUnmounted, signout]);

sample({
  clock: setFilters,
  target: $filters,
});

/** Если не истек период неактивности, то обновляется timestamp */
sample({
  clock: pageMounted,
  source: $token,
  filter: (token) => {
    const currentTimestamp = new Date().getTime();

    const { timestamp } = JSON.parse(localStorage.getItem(name) as string)[
      token as string
    ];

    return Boolean(token) && currentTimestamp - timestamp <= timeOfInactivity;
  },
  fn: (token) => {
    const values = JSON.parse(localStorage.getItem(name) as string);
    const timestamp = new Date().getTime();

    values[token as string].timestamp = timestamp;

    return {
      key: name,
      data: JSON.stringify(values),
    };
  },
  target: fxSetLocaleStorage,
});

/** Если превышен период неактивности, то очищаются сохраненные фильтры */
sample({
  clock: pageMounted,
  source: $token,
  filter: (token) => {
    const currentTimestamp = new Date().getTime();

    const { timestamp } = JSON.parse(localStorage.getItem(name) as string)[
      token as string
    ];

    return Boolean(token) && currentTimestamp - timestamp > timeOfInactivity;
  },
  fn: (token) => ({
    key: name,
    data: JSON.stringify(excludeFilters(token as string)),
  }),
  target: fxSetLocaleStorage,
});
